import { ArrowBack as IconArrowBack } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import GaiaConfirmActionDialog from "~/modules/app/components/GaiaConfirmActionDialog";
import GaiaPagination from "~/modules/app/components/GaiaPagination";
import useDestructiveActionBlocker from "~/modules/app/hooks/useDestructiveActionBlocker";
import { MapPanel, MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import { useSessionStorageSearchParams } from "~/shared/hooks/useSessionStorageSearchParams";

import {
    ProjectReviewVisitV2ObservationList,
    ProjectReviewVisitV2ObservationListSkeleton,
} from "../../../../components/ProjectReviewVisitV2ObservationList";
import { useProjectReviewVisitV2ObservationList_BulkApprove } from "./api/useProjectReviewVisitV2ObservationList_BulkApprove";
import { useProjectReviewVisitV2ObservationList_GetData } from "./api/useProjectReviewVisitV2ObservationList_GetData";

export function ProjectReviewVisitV2ObservationListRoute() {
    const { t } = useTranslation("project");
    const { visitId } = useParams();

    const [searchParams, setSearchParams] = useSessionStorageSearchParams({ bucket: visitId });
    const page = Number(searchParams.get("page") ?? "0");
    const pageSize = 5;
    const layerId = searchParams.get("layerId") ?? undefined;

    const { observations, hasAllObservationsApproved, queryInfo, loading } = useProjectReviewVisitV2ObservationList_GetData(
        visitId as string,
        pageSize,
        page,
        layerId,
    );

    const { bulkApprove, loading: bulkApproveLoading } = useProjectReviewVisitV2ObservationList_BulkApprove(visitId as string);
    const bulkApproveBlocker = useDestructiveActionBlocker(bulkApprove);
    const handlePageChange = useCallback(
        (page: number) =>
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set("page", page.toString());
                return next;
            }),
        [setSearchParams],
    );

    const createLinkToObservation = (observation: NonNullable<typeof observations>[number]) => {
        return `./${observation.featureId}`;
    };

    if (loading) {
        return <ProjectReviewVisitV2ObservationListSkeleton backHref=".." />;
    }

    return (
        <>
            <GaiaConfirmActionDialog
                title={t("projectReviewVisitV2.observationsRoute.bulkApproveDialog.title")}
                description={t("projectReviewVisitV2.observationsRoute.bulkApproveDialog.description")}
                blocker={bulkApproveBlocker}
            />
            <MapPanel>
                <MapPanelHeader>
                    <IconButton component={Link} color="inherit" to="..">
                        <IconArrowBack />
                    </IconButton>

                    <Stack paddingBlock={1} flexGrow={1}>
                        <Typography variant="h6">{t("projectReviewVisitV2.observationsRoute.title")}</Typography>
                    </Stack>
                </MapPanelHeader>

                <MapPanelScrollableArea sx={{ padding: 0 }}>
                    <ProjectReviewVisitV2ObservationList createTo={createLinkToObservation} observations={observations ?? []} />
                    {queryInfo && (
                        <GaiaPagination
                            sx={{ display: "flex", justifyContent: "center", paddingBlock: 2 }}
                            page={page}
                            onPageChange={handlePageChange}
                            total={queryInfo.paginationOption.total ?? 0}
                            pageSize={pageSize}
                        />
                    )}
                </MapPanelScrollableArea>
                {hasAllObservationsApproved === false && (
                    <Box
                        padding={2}
                        sx={theme => ({
                            borderTop: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Button fullWidth variant="contained" onClick={bulkApproveBlocker.onAction} disabled={bulkApproveLoading}>
                            {bulkApproveLoading
                                ? t("projectReviewVisitV2.observationsRoute.bulkApproveButtonLoading")
                                : t("projectReviewVisitV2.observationsRoute.bulkApproveButton")}
                        </Button>
                    </Box>
                )}
            </MapPanel>
        </>
    );
}
